import { MATRIX_ARCANA, ZodiacSign } from '@/api/consts'
import type { SynastryV2Response } from '@/api/schema/synastry'
import { Flex, Row } from '@/app/components/Flex/Flex'
import { Sign } from '@/app/components/Sign/Sign'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import type { PLANET_RAW_SVG_MAP } from '@/app/utils/natal'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import styles from './index.module.scss'

type PlanetKey =
  | 'SUN'
  | 'MOON'
  | 'MERCURY'
  | 'VENUS'
  | 'MARS'
  | 'JUPITER'
  | 'SATURN'
  | 'URANUS'
  | 'NEPTUNE'
  | 'PLUTO'

type MatrixKey =
  | 'center'
  | 'relationshipCenter'
  | 'money'
  | 'comfort'
  | 'future'

type Planet = {
  key: PlanetKey
  label: string
  icon: keyof typeof PLANET_RAW_SVG_MAP
}

const PLANETS: Planet[] = [
  { key: 'SUN', label: 'Солнце', icon: 'Sun' },
  { key: 'MOON', label: 'Луна', icon: 'Moon' },
  { key: 'VENUS', label: 'Венера', icon: 'Venus' },
  { key: 'MERCURY', label: 'Меркурий', icon: 'Mercury' },
  { key: 'MARS', label: 'Марс', icon: 'Mars' },
  { key: 'SATURN', label: 'Сатурн', icon: 'Saturn' },
  // { key: 'JUPITER', label: 'Юпитер', icon: 'Jupiter' },
  // { key: 'URANUS', label: 'Уран', icon: 'Uranus' },
  // { key: 'NEPTUNE', label: 'Нептун', icon: 'Neptune' },
  // { key: 'PLUTO', label: 'Плутон', icon: 'Pluto' },
]

type Point = {
  key: MatrixKey
  label: string
}

const MATRIX_POINTS: Point[] = [
  { key: 'center', label: 'Личность' },
  { key: 'relationshipCenter', label: 'Отношения' },
  { key: 'money', label: 'Деньги' },
  { key: 'comfort', label: 'Комфорт' },
  { key: 'future', label: 'Будущее' },
]

const SIGNS: Record<ZodiacSign, { label: string; sign: ZodiacSign }> = {
  [ZodiacSign.ARIES]: { label: 'Овен', sign: ZodiacSign.ARIES },
  [ZodiacSign.TAURUS]: { label: 'Телец', sign: ZodiacSign.TAURUS },
  [ZodiacSign.GEMINI]: { label: 'Близнецы', sign: ZodiacSign.GEMINI },
  [ZodiacSign.CANCER]: { label: 'Рак', sign: ZodiacSign.CANCER },
  [ZodiacSign.LEO]: { label: 'Лев', sign: ZodiacSign.LEO },
  [ZodiacSign.VIRGO]: { label: 'Дева', sign: ZodiacSign.VIRGO },
  [ZodiacSign.LIBRA]: { label: 'Весы', sign: ZodiacSign.LIBRA },
  [ZodiacSign.SCORPIO]: { label: 'Скорпион', sign: ZodiacSign.SCORPIO },
  [ZodiacSign.SAGITTARIUS]: { label: 'Стрелец', sign: ZodiacSign.SAGITTARIUS },
  [ZodiacSign.CAPRICORN]: { label: 'Козерог', sign: ZodiacSign.CAPRICORN },
  [ZodiacSign.AQUARIUS]: { label: 'Водолей', sign: ZodiacSign.AQUARIUS },
  [ZodiacSign.PISCES]: { label: 'Рыбы', sign: ZodiacSign.PISCES },
}

const getSign = (key: string) => {
  const signKey = key.split('_').at(-1)

  if (!signKey || !(signKey in SIGNS)) {
    return null
  }

  return SIGNS[signKey as ZodiacSign]
}

const getArcana = (key: string) => {
  const arcana = Number(key.split('_').at(-1))

  if (!Number.isInteger(arcana)) {
    return null
  }

  return {
    value: arcana,
    name: MATRIX_ARCANA[arcana]?.name ?? String(arcana),
  }
}

export const Compare: FunctionalComponent<{
  data: SynastryV2Response
}> = props => {
  const astroRows = props.data.type === 'astro'
    ? PLANETS
      .map((planet) => {
        const person1 = props.data.compare1[planet.key]
        const person2 = props.data.compare2[planet.key]

        if (!person1 || !person2) {
          return null
        }

        return {
          planet,
          person1,
          person2,
          person1Sign: getSign(person1.key),
          person2Sign: getSign(person2.key),
        }
      })
      .filter(row => row !== null)
    : []

  const matrixRows = props.data.type === 'matrix'
    ? MATRIX_POINTS
      .map((point) => {
        const person1 = props.data.compare1[point.key]
        const person2 = props.data.compare2[point.key]

        if (!person1 || !person2) {
          return null
        }

        return {
          point,
          person1,
          person2,
        }
      })
      .filter(row => row !== null)
    : []

  if (astroRows.length === 0 && matrixRows.length === 0) {
    return null
  }

  return (
    <div class={styles.root}>
      <UI.Panel class={styles.panel}>
        <div class={classNames(styles.row, styles.head)}>
          <div class={styles.cell}>
            <UI.Panel type='ghost'>
              <Text
                upper
                fontSize={14}
                fontSizeMobile={13}
                fontWeight={600}
                lineHeight={1}
              >
                {props.data.persone1Name}
              </Text>
            </UI.Panel>
          </div>
          <div class={styles.cell}>
            <UI.Panel type='ghost'>
              <Text
                upper
                fontSize={14}
                fontSizeMobile={13}
                fontWeight={600}
                lineHeight={1}
              >
                {props.data.persone2Name}
              </Text>
            </UI.Panel>
          </div>
        </div>

        {astroRows.map(row => (
          <div class={styles.row} key={row.planet.key}>
            <CompareAstroCell
              planet={row.planet}
              title={row.person1.title}
              text={row.person1.text}
              sign={row.person1Sign}
            />

            <CompareAstroCell
              title={row.person2.title}
              text={row.person2.text}
              sign={row.person2Sign}
            />
          </div>
        ))}

        {matrixRows.map(row => (
          <div class={styles.row} key={row.point.key}>
            <CompareMatrixCell
              point={row.point}
              arcana={getArcana(row.person1.key)}
              text={row.person1.text}
            />

            <CompareMatrixCell
              arcana={getArcana(row.person2.key)}
              text={row.person2.text}
            />
          </div>
        ))}
      </UI.Panel>
    </div>
  )
}

const CompareAstroCell: FunctionalComponent<{
  planet?: Planet
  title: string
  text: string
  sign: { label: string; sign: ZodiacSign } | null
}> = props => {
  return (
    <div class={styles.cell}>
      <UI.Panel type='ghost'>
        <Row gap={5}>
          <Text fontSize={13} opacity={0.5}>
            {props.planet?.label ?? <>&#8203;</>}
          </Text>

          {props.sign && (
            <Flex dir='row' gap={4} align='center'>
              <span class={styles.sign}>
                <Sign
                  sign={props.sign.sign}
                  class={styles.signIcon}
                />
              </span>
              <Text fontSize={14} fontWeight={600} fontSizeMobile={13}>
                {props.sign.label}
              </Text>
            </Flex>
          )}

          <Text fontSize={14} fontSizeMobile={13}>
            {props.text}
          </Text>
        </Row>
      </UI.Panel>
    </div>
  )
}

const CompareMatrixCell: FunctionalComponent<{
  point?: Point
  arcana: { value: number; name: string } | null
  text: string
}> = props => {
  return (
    <div class={styles.cell}>
      <UI.Panel type='ghost'>
        <Row gap={5}>
          <Text fontSize={13} opacity={0.5}>
            {props.point?.label ?? <>&#8203;</>}
          </Text>

          {props.arcana && (
            <Flex dir='row' gap={4} align='center'>
              <div class={styles.sign}>
                {props.arcana.value}
              </div>
              <Text fontSize={14} fontWeight={600} fontSizeMobile={13}>
                {props.arcana.name}
              </Text>
            </Flex>
          )}

          <Text fontSize={14} fontSizeMobile={13}>
            {props.text}
          </Text>
        </Row>
      </UI.Panel>
    </div>
  )
}
